/* StoreScale A/B — theme harness
 *
 * Measures the theme's A/B tests through the StoreScale app:
 *
 *   1. Sticky per-visitor assignment (localStorage). For the sitewide
 *      bxgy_bundle test the arm is assigned 50/50 by the inline <head> script in
 *      theme.liquid (window.OSC_ARM + ss-bxgy-a|b class) BEFORE anything renders;
 *      this harness reads that same localStorage key.
 *   2. Records an exposure per SESSION (unique visitor, server-deduped) via a
 *      beacon — the accurate denominator, comparable to Shopify "sessions".
 *   3. Writes the variant into the cart as `_exp_<id>` so it rides onto the
 *      order webhook + checkout web pixel (the accurate numerator).
 *   4. Hooks every add-to-cart code path and reports the funnel step.
 *
 * ?arm=a|b (or ?ss_arm=) on any URL forces + persists an arm for QA/campaigns —
 * handled by the theme.liquid head script so rendering and tracking agree.
 * Forced visitors (email blasts, QA) were NOT randomized: the head script sets
 * ss_exp_<id>_f, and this harness marks their beacons/orders `forced` so the
 * Worker can keep them OUT of the A/B stats (reported as a separate segment).
 */
(function () {
  var APP = 'https://storescale-ab.julien-b1d.workers.dev';
  var VKEY = 'ss_vid';

  function uuid() {
    return (crypto.randomUUID && crypto.randomUUID()) ||
      'v-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }
  function visitorId() {
    var v;
    try { v = localStorage.getItem(VKEY); } catch (e) {}
    if (!v) { v = uuid(); try { localStorage.setItem(VKEY, v); } catch (e) {} }
    return v;
  }

  // IMPORTANT: beacons are sent as text/plain (a CORS-safelisted content type).
  // A cross-origin sendBeacon with an application/json Blob is dropped by the
  // browser because it can't preflight — which silently under-counts exposures
  // while same-origin cart tagging still succeeds (orders > exposures). The
  // Worker parses the body with request.json() regardless of the content-type.
  function beacon(path, payload) {
    var body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon &&
          navigator.sendBeacon(APP + path, new Blob([body], { type: 'text/plain' }))) {
        return; // queued successfully
      }
    } catch (e) {}
    try {
      fetch(APP + path, { method: 'POST', keepalive: true, mode: 'cors',
        headers: { 'content-type': 'text/plain' }, body: body });
    } catch (e) {}
  }

  var SS = {
    visitor: visitorId(),
    // id -> assigned variant for this visitor, for other scripts on the page.
    assigned: {},

    /** True if this visitor's arm was ever FORCED (?arm= / email link) rather
     *  than randomized — such visitors are excluded from the experiment math. */
    isForced: function (id) {
      try { return localStorage.getItem('ss_exp_' + id + '_f') === '1'; } catch (e) { return false; }
    },

    /** Synchronous arm accessor: 'A'|'B', never null. Prefers the arm the
     *  inline <head> script already resolved (window.OSC_ARM — handles ?arm=
     *  forcing before first paint); otherwise reads the sticky localStorage
     *  value, performing the same 50/50 sticky assignment init() uses when the
     *  visitor has none yet. Offer UIs branch on this before rendering. */
    arm: function (id) {
      id = String(id || '');
      if (id === 'bxgy_bundle' && (window.OSC_ARM === 'A' || window.OSC_ARM === 'B')) {
        return window.OSC_ARM;
      }
      return this.variantOf(id) || this.ensure(id);
    },

    /** Return this visitor's sticky variant for an experiment ('A'|'B'|null). */
    variantOf: function (id) {
      if (this.assigned[id]) return this.assigned[id];
      try {
        var v = localStorage.getItem('ss_exp_' + id);
        if (v === 'A' || v === 'B') { this.assigned[id] = v; return v; }
      } catch (e) {}
      return null;
    },

    /** Sticky client-side assignment WITHOUT counting an exposure. Safe to call
     *  on every page (e.g. globally) so the variant flag is always available. */
    ensure: function (id, split) {
      id = String(id || '');
      if (!id) return 'A';
      split = typeof split === 'number' ? split : 0.5;
      var key = 'ss_exp_' + id;
      var v = null;
      try { v = localStorage.getItem(key); } catch (e) {}
      if (v !== 'A' && v !== 'B') {
        v = Math.random() < split ? 'B' : 'A';
        try { localStorage.setItem(key, v); } catch (e) {}
      }
      this.assigned[id] = v;
      return v;
    },

    /** Count an exposure + tag the cart for a client-assigned experiment.
     *  opts = { id, split, onReady }. Exposures are deduped server-side per
     *  (experiment, visitor), so calling this on every page counts sessions. */
    client: function (opts) {
      opts = opts || {};
      var id = String(opts.id || '');
      if (!id) return 'A';
      var v = this.ensure(id, opts.split);
      this.expose(id, v);
      this.tagCart(id, v);
      if (typeof opts.onReady === 'function') { try { opts.onReady(v); } catch (e) {} }
      return v;
    },

    /** Sitewide entry point (called from theme.liquid on every page). The arm
     *  was already assigned by the inline <head> script; this records ONE
     *  session exposure per visitor (server-deduped), tags the cart so the arm
     *  rides onto the order + checkout pixel, and starts add-to-cart tracking. */
    init: function (id, split) {
      id = String(id || '');
      if (!id) return;
      var arm = this.variantOf(id) || this.ensure(id, split);
      this.expose(id, arm);
      this.tagCart(id, arm);
      this._hookAtc(id);
    },

    /** Fire a funnel event ('add_to_cart', 'bundle', ...). Deduped server-side
     *  per (experiment, visitor, type). `variant` is optional — defaults to the
     *  visitor's sticky arm. */
    event: function (id, type, variant) {
      id = String(id || ''); type = String(type || '');
      if (!id || !type) return;
      if (variant !== 'A' && variant !== 'B') variant = this.variantOf(id) || 'A';
      beacon('/event', { experimentId: id, variant: variant, visitorId: this.visitor, type: type,
        forced: this.isForced(id) });
    },

    // Convenience: record a completed bundle add-to-cart for an experiment.
    bundle: function (id, variant) { this.event(id, 'bundle', variant); },

    /** Wire up one experiment for the current page (server-rendered A/B).
     *  opts = { id, serverVariant, splitB } — see track() docs in git history. */
    track: function (opts) {
      opts = opts || {};
      var id = String(opts.id || '');
      if (!id) return;
      var serverVariant = opts.serverVariant === 'B' ? 'B' : 'A';
      var key = 'ss_exp_' + id;

      var sticky = null;
      try { sticky = localStorage.getItem(key); } catch (e) {}

      // First view for this visitor: adopt whatever the server showed. No reload.
      if (sticky !== 'A' && sticky !== 'B') {
        sticky = serverVariant;
        try { localStorage.setItem(key, sticky); } catch (e) {}
      }

      // Returning visitor landed on the other arm → correct it once, then stop.
      if (sticky !== serverVariant) {
        var guard = 'ss_fix_' + id;
        var fixedThisLoad = false;
        try { fixedThisLoad = sessionStorage.getItem(guard) === '1'; } catch (e) {}
        if (!fixedThisLoad) {
          try { sessionStorage.setItem(guard, '1'); } catch (e) {}
          var u = new URL(window.location.href);
          u.searchParams.set('utm_content', 'variation' + sticky);
          window.location.replace(u.toString());
          return; // page reloads in the assigned arm; exposure fires after that
        }
        // Guard already set (e.g. redirects blocked): fall through and measure
        // what was actually shown rather than loop.
        sticky = serverVariant;
      } else {
        try { sessionStorage.removeItem('ss_fix_' + id); } catch (e) {}
      }

      this.assigned[id] = sticky;
      this.expose(id, sticky);
      this.tagCart(id, sticky);
      // Keep the variant in the URL for downstream analytics (matches old behavior).
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get('utm_content') !== 'variation' + sticky) {
          url.searchParams.set('utm_content', 'variation' + sticky);
          window.history.replaceState({}, '', url.pathname + '?' + url.searchParams.toString() + url.hash);
        }
      } catch (e) {}
    },

    expose: function (id, variant) {
      beacon('/beacon', { experimentId: id, variant: variant, visitorId: this.visitor,
        forced: this.isForced(id) });
    },

    // Persist the variant onto the cart so it flows to the order webhook.
    tagCart: function (id, variant) {
      var attrs = {};
      attrs['_exp_' + id] = variant;
      attrs['_exp_visitor'] = this.visitor;
      // Forced-arm visitors (email / QA) are flagged so their orders are
      // segmented out of the A/B stats server-side.
      attrs['_exp_forced'] = this.isForced(id) ? '1' : '0';
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        });
      } catch (e) {}
    },

    // Detect any add-to-cart (all theme code paths) and report it once/visitor.
    _hookAtc: function (id) {
      if (this._atcHooked) return; this._atcHooked = true;
      var self = this;
      var isAdd = function (u) { try { return /\/cart\/add(\.js)?(\?|$)/.test(String(u)); } catch (e) { return false; } };
      var fire = function () { self.event(id, 'add_to_cart'); };
      try {
        var of = window.fetch;
        if (of && !of._ssWrapped) {
          window.fetch = function (input) {
            if (isAdd(typeof input === 'string' ? input : (input && input.url))) fire();
            return of.apply(this, arguments);
          };
          window.fetch._ssWrapped = true;
        }
      } catch (e) {}
      try {
        var oo = XMLHttpRequest.prototype.open;
        if (oo && !oo._ssWrapped) {
          XMLHttpRequest.prototype.open = function (m, u) { if (isAdd(u)) fire(); return oo.apply(this, arguments); };
          XMLHttpRequest.prototype.open._ssWrapped = true;
        }
      } catch (e) {}
    }
  };

  window.StoreScaleAB = SS;
})();
